import Vue from 'vue'
import VueRouter from 'vue-router'
import Home from '@/pages/Home'
import Cookies from 'js-cookie'
import { getLfAdminRedirect, hasLfAdminSession, isLfAdminHost } from '@/utils/lfAdminSession'

const Login = () => import('@/pages/Login')
const AdminLogin = () => import('@/pages/AdminLogin')
const LFAdminLayout = () => import('@/pages/LFAdmin/Layout.vue')
const Square = () => import('@/pages/Square')
const NewPost = () => import('@/pages/NewPost')
const MyCryptic = () => import('@/pages/MyCryptic')
const PostDetail = () => import('@/pages/PostDetail')
const People = () => import('@/pages/People')
const Pay = () => import('@/pages/Pay')
const BrandHome = () => import('@/pages/BrandHome')
const MyKnowits = () => import('@/pages/MyKnowits')
const MyAcquisitions = () => import('@/pages/MyAcquisitions')
const SubscriptionOrder = () => import('@/pages/SubscriptionOrder')
const Receipt = () => import('@/pages/MyAcquisitions/receiptT.vue')

Vue.use(VueRouter)

const routes = [
  {
    path: '/signup',
    name: 'Signup',
    component: () => import('@/pages/Signup/index.vue'),
    meta: {
      requiresAuth: false,
      title: 'Sign Up'
    }
  },
  // /about moved to child route of Home layout
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: () => import('@/pages/PrivacyPolicy/index.vue'),
    meta: {
      requiresAuth: false,
      title: 'Privacy Policy'
    }
  },
  {
    path: '/terms-of-service',
    name: 'TermsOfService',
    component: () => import('@/pages/TermsOfService/index.vue'),
    meta: {
      requiresAuth: false,
      title: 'Terms of Service'
    }
  },
  {
    path: '/subscription-terms',
    name: 'SubscriptionTerms',
    component: () => import('@/pages/SubscriptionTerms/index.vue'),
    meta: {
      requiresAuth: false,
      title: 'Subscription Terms'
    }
  },
  {
    path: '/admin-login',
    name: 'LFAdminLogin',
    component: AdminLogin,
    meta: {
      requiresAuth: false,
      title: 'LF Admin Login'
    }
  },
  {
    path: '/creator-entry',
    name: 'CreatorEntry',
    component: () => import('@/pages/CreatorEntry/index.vue'),
    meta: {
      requiresAuth: false,
      title: 'Creator Entry'
    }
  },
  {
    path: '/lf-admin',
    component: LFAdminLayout,
    redirect: '/lf-admin/dashboard',
    meta: {
      requiresLfAdmin: true,
      title: 'LF Admin'
    },
    children: [
      {
        path: 'curators',
        name: 'LFAdminCurators',
        component: () => import('@/pages/LFAdmin/curators/index.vue'),
        meta: { requiresLfAdmin: true, title: 'Curator Accounts' }
      },
      {
        path: 'collections',
        name: 'LFAdminCollections',
        component: () => import('@/pages/LFAdmin/collections/index.vue'),
        meta: { requiresLfAdmin: true, title: 'Collection Pipeline' }
      },
      {
        path: 'dashboard',
        name: 'LFAdminDashboard',
        component: () => import('@/pages/LFAdmin/dashboard/index.vue'),
        meta: {
          requiresLfAdmin: true,
          title: 'Dashboard'
        }
      },
      {
        path: 'user-behavior',
        name: 'LFAdminUserBehavior',
        component: () => import('@/pages/LFAdmin/userBehavior/index.vue'),
        meta: { requiresLfAdmin: true, title: 'User Behavior' }
      },
      {
        path: 'model-config',
        name: 'LFAdminModelConfig',
        component: () => import('@/pages/LFAdmin/modelConfig/index.vue'),
        meta: {
          requiresLfAdmin: true,
          title: 'Model Config'
        }
      },
      {
        path: 'reading-activity',
        name: 'LFAdminReadingActivity',
        component: () => import('@/pages/LFAdmin/readingActivity/index.vue'),
        meta: {
          requiresLfAdmin: true,
          title: 'Reading Activity'
        }
      },
      {
        path: 'account-access',
        name: 'LFAdminAccountAccess',
        component: () => import('@/pages/LFAdmin/accountAccess/index.vue'),
        meta: {
          requiresLfAdmin: true,
          title: 'Account Access'
        }
      },
      {
        path: 'educator-proposals',
        name: 'LFAdminEducatorProposals',
        component: () => import('@/pages/LFAdmin/educators/Proposals.vue'),
        meta: { requiresLfAdmin: true, title: 'Teacher Proposals' }
      },
      {
        path: 'educators',
        name: 'LFAdminEducators',
        component: () => import('@/pages/LFAdmin/educators/index.vue'),
        meta: { requiresLfAdmin: true, title: 'Educator Programs' }
      },
      {
        path: 'editor-bundles',
        name: 'LFAdminEditorBundles',
        component: () => import('@/pages/LFAdmin/editorBundles/index.vue'),
        meta: {
          requiresLfAdmin: true,
          title: 'Editor Bundles'
        }
      },
      {
        path: 'idea-proposals',
        name: 'LFAdminIdeaProposals',
        component: () => import('@/pages/LFAdmin/ideaProposals/index.vue'),
        meta: { requiresLfAdmin: true, title: 'Idea Proposals' }
      },
      {
        path: 'special-labels',
        name: 'LFAdminSpecialLabels',
        component: () => import('@/pages/LFAdmin/specialLabels/index.vue'),
        meta: { requiresLfAdmin: true, title: 'Special Labels' }
      },
      {
        path: 'track',
        name: 'LFAdminTrack',
        component: () => import('@/pages/LFAdmin/lfTrack/index.vue'),
        meta: {
          requiresLfAdmin: true,
          title: 'LF Track'
        }
      },
      {
        path: 'ai-search-review',
        name: 'LFAdminAiSearchReview',
        component: () => import('@/pages/LFAdmin/aiSearchReview/index.vue'),
        meta: {
          requiresLfAdmin: true,
          title: 'AI Search Review'
        }
      },
      {
        path: 'contact-submissions',
        name: 'LFAdminContactSubmissions',
        component: () => import('@/pages/LFAdmin/contactSubmissions/index.vue'),
        meta: {
          requiresLfAdmin: true,
          title: 'Contact Submissions'
        }
      },
      {
        path: 'platform-billing',
        name: 'LFAdminPlatformBilling',
        component: () => import('@/pages/LFAdmin/platformBilling/index.vue'),
        meta: {
          requiresLfAdmin: true,
          title: 'Platform Billing'
        }
      },
      {
        path: 'vouchers',
        name: 'LFAdminVouchers',
        component: () => import('@/pages/LFAdmin/vouchers/index.vue'),
        meta: {
          requiresLfAdmin: true,
          title: 'Vouchers'
        }
      },
      {
        path: 'affiliate-program',
        name: 'LFAdminAffiliateProgram',
        component: () => import('@/pages/LFAdmin/affiliate/index.vue'),
        meta: {
          requiresLfAdmin: true,
          title: 'Affiliate Program'
        }
      },
      {
        path: 'markdown-stats',
        name: 'LFAdminMarkdownStats',
        component: () => import('@/pages/LFAdmin/markdownStats/index.vue'),
        meta: {
          requiresLfAdmin: true,
          title: 'Markdown Stats'
        }
      },
    ]
  },
  {
    path: '/',
        name: 'Home',
        component: Home,
        redirect: '/my-curation',
        meta: {
            requiresAuth: true,
        },
        children: [
            {
                path: 'about',
                name: 'BrandHome',
                component: BrandHome,
                meta: {
                    requiresAuth: false,
                    title: 'About LearningFirst',
                }
            },
            {
                path: 'my-collections',
                name: 'MyCollections',
                component: () => import('@/pages/MyCollections/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "My Collections",
                }
            },
            {
                path: 'home',
                name: 'Home',
                component: () => import('@/pages/MyCollections/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "My Collections",
                }
            },
            {
                path: 'square',
                name: 'SquareAlt',
                component: Square,
                meta: {
                    requiresAuth: true,
                    title: "Square",
                }
            },
            {
                path: 'plaza',
                name: 'Plaza',
                component: () => import('@/pages/Plaza/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Plaza",
                }
            },
            {
                path: 'people',
                name: 'People',
                component: People,
                meta: {
                    requiresAuth: true,
                    title: "People",
                }
            },
            {
                path: 'my-knowit',
                name: 'MyKnowit',
                component: MyCryptic,
                meta: {
                    requiresAuth: true,
                    title: "My Knowit",
                }
            },
            {
                path: 'new-knowit',
                name: 'NewPost',
                component: NewPost,
                meta: {
                    requiresAuth: true
                }
            },
            {
                path: 'edit-stack',
                name: 'EditPost',
                component: NewPost,
                meta: {
                    requiresAuth: true
                }
            },
            {
                path: 'stack',
                name: 'PostDetail',
                component: PostDetail,
                meta: {
                    requiresAuth: true
                }
            },
            {
                path: 'my-curation',
                name: 'MyCuration',
                component: () => import('@/pages/MyKnowits/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "My Curation",
                }
            },
            {
                path: 'my-knowits',
                name: 'MyKnowits',
                component: () => import('@/pages/MyKnowitCards/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "My Knowits",
                }
            },
            {
                path: 'starred-items',
                name: 'StarredItems',
                component: () => import('@/pages/StarredItems/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Starred Items",
                }
            },
            {
                path: 'my-learning',
                name: 'MyLearning',
                component: () => import('@/pages/MyLearning/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "My Learning",
                }
            },
            {
                path: 'my-certificates',
                name: 'MyCertificates',
                component: () => import('@/pages/MyCertificates/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "My Certificates",
                }
            },
            {
                path: 'exam-prep',
                name: 'ExamPrep',
                component: () => import('@/pages/ExamPrep/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Exam Preparation",
                }
            },
            {
                path: 'exam',
                name: 'ExamTaking',
                component: () => import('@/pages/ExamTaking/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Exam",
                }
            },
            {
                path: 'exam-result',
                name: 'ExamResult',
                component: () => import('@/pages/ExamResult/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Exam Result",
                }
            },
            {
                path: 'my-pulses',
                name: 'MyPulses',
                component: () => import('@/pages/MyPulses/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "My Pulses",
                }
            },
            {
                path: 'curator-followers',
                name: 'CuratorFollowers',
                component: () => import('@/pages/CuratorFollowers/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Followers",
                }
            },
            {
                path: 'my-acquisitions',
                name: 'MyAcquisitions',
                component: () => import('@/pages/MyAcquisitions/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "My Acquisitions",
                }
            },
            {
                path: 'vouchers',
                name: 'MyVouchers',
                component: () => import('@/pages/MyVouchers/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Vouchers",
                }
            },
            {
                path: 'my-collection',
                name: 'MyCollection',
                component: () => import('@/pages/MyCollection/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "My Collection",
                }
            },
            {
                path: 'new-collection',
                name: 'NewCollection',
                component: () => import('@/pages/NewCollection/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "New Collection",
                }
            },
            {
                path: 'edit-deck',
                name: 'EditDeck',
                component: () => import('@/pages/NewCollection/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Edit Deck",
                }
            },
            {
                path: 'deck',
                name: 'DeckDetail',
                component: () => import('@/pages/CollectionDetail/index.vue'),
                meta: {
                    requiresAuth: false,
                    title: "Deck Detail",
                }
            },
            {
                path: 'personal',
                name: 'Personal',
                component: () => import('@/pages/Personal/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Personal",
                }
            },
            {
                path: 'profile',
                name: 'Profile',
                component: () => import('@/pages/Profile/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Profile"
                }
            },
            {
                path: 'my-activity',
                name: 'MyActivity',
                component: () => import('@/pages/MyActivity/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "My Activity"
                }
            },
            {
                path: 'inbox',
                name: 'Inbox',
                component: () => import('@/pages/Inbox/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Inbox",
                }
            },
            {
                path: 'conversations',
                name: 'Conversations',
                component: () => import('@/pages/Conversations/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Conversations",
                }
            },
            {
                path: 'collection-order',
                name: 'CollectionOrder',
                component: () => import('@/pages/CollectionOrder/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Collection Order",
                }
            },
            {
                path: 'collection-orders',
                name: 'CollectionOrderList',
                component: () => import('@/pages/CollectionOrderList/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Collection Orders",
                }
            },
            {
                path: 'read-collection',
                name: 'ReadCollection',
                component: () => import('@/pages/ReadCollection/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Read Collection",
                    autoHideNav: true,
                }
            },
            {
                path: 'new-bundle',
                name: 'NewBundle',
                component: () => import('@/pages/NewBundle/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "New Bundle"
                }
            },
            {
                path: 'edit-bundle/:id',
                name: 'EditBundle',
                component: () => import('@/pages/NewBundle/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Edit Bundle"
                }
            },
            {
                path: 'bundle',
                name: 'BundleDetail',
                component: () => import('@/pages/BundleDetail/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Bundle Detail",
                }
            },
            {
                path: 'bundle-order',
                name: 'BundleOrder',
                component: () => import('@/pages/BundleOrder/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Bundle Order",
                }
            },
            {
                path: '/post-order',
                name: 'PostOrder',
                component: () => import('@/pages/PostOrder')
            },
            {
                path: '/subscription-order',
                name: 'SubscriptionOrder',
                component: SubscriptionOrder,
                meta: {
                  requiresAuth: true
                }
            },
            {
                path: '/receipt',
                name: 'Receipt',
                component: Receipt,
                meta: {
                  requiresAuth: true
                }
            },
            {
                path: 'search',
                name: 'SearchResults',
                component: () => import('@/pages/SearchResults/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Search Results",
                }
            },
            {
                path: 'workflows',
                name: 'Workflows',
                component: () => import('@/pages/Workflows/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "My Workflows",
                }
            },
            {
                path: 'task-canvas',
                name: 'TaskCanvas',
                component: () => import('@/pages/TaskCanvas/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Task Canvas",
                }
            },
            {
                path: 'youtube-transcript',
                name: 'YouTubeTranscript',
                component: () => import('@/pages/YouTubeTranscript/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "YouTube Summarizer",
                }
            },
            {
                path: 'platform-subscription',
                name: 'PlatformSubscription',
                component: () => import('@/pages/PlatformSubscription/index.vue'),
                meta: {
                    requiresAuth: true,
                    title: "Platform Subscription",
                }
            },
        ]
    },
    {
        path: '/login',
        name: 'Login',
        component: Login
    },
    {
        path: '/onboarding',
        name: 'Onboarding',
        component: () => import('@/pages/Onboarding/index.vue'),
        meta: {
            requiresAuth: true,
            title: 'Welcome'
        }
    },
    {
        path: '/pay',
        name: 'Pay',
        component: Pay,
        meta: {
            requiresAuth: true,
        },
    },
    {
        path: '/video-player',
        name: 'VideoPlayer',
        component: () => import('@/pages/VideoPlayer/index.vue'),
        meta: {
            requiresAuth: true,
            title: 'Video Player',
        },
    },
    {
        path: '/shorts',
        name: 'ShortsPlayer',
        component: () => import('@/pages/ShortsPlayer/index.vue'),
        meta: {
            requiresAuth: true,
            title: 'Shorts Player',
        },
    },
]

const router = new VueRouter({
    linkActiveClass: "active",
    routes,
});

router.beforeEach((to, from, next) => {
    const requiresLfAdmin = to.matched.some(record => record.meta && record.meta.requiresLfAdmin)
    if (to.path === "/admin-login") {
        if (hasLfAdminSession()) {
            next(getLfAdminRedirect(to.query.redirect));
            return;
        }
        next();
        return;
    }

    if (isLfAdminHost() && !requiresLfAdmin) {
        next('/lf-admin/dashboard');
        return;
    }

    if (requiresLfAdmin) {
        if (hasLfAdminSession()) {
            next();
            return;
        }
        next({
            path: "/admin-login",
            query: { redirect: to.fullPath }
        });
        return;
    }

    if (to.path === "/login") {
        next();
        return;
    }

    const userId = Cookies.get('user_id');
    const hasAuthToken = to.query.auth;

    if (!to.meta.requiresAuth) {
        next();
        return;
    }

    if (to.name === 'ReadCollection' && hasAuthToken) {
        next();
        return;
    }

    if (userId && userId !== "") {
        next();
        return;
    }

    next({
        path: "/login",
        query: { redirect: to.fullPath }
    });
});

export default router;
